'use client';

import React from 'react';
import TopNavigation from './top-navigation';
import styles from './authenticated-layout.module.css';

interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

interface ActionButton {
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary';
}

interface AuthenticatedLayoutProps {
  user: { Email: string; Name: string; Role: string };
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  buttons?: ActionButton[];
  onLogout?: () => void;
  children: React.ReactNode;
}

export default function AuthenticatedLayout({
  user,
  title,
  subtitle,
  breadcrumbs,
  buttons,
  onLogout,
  children,
}: AuthenticatedLayoutProps) {
  return (
    <div className={styles.authenticatedContainer}>
      {/* Top Navigation */}
      <TopNavigation user={user} onLogout={onLogout} />

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Page Header with Breadcrumbs and Actions */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.titleSection}>
              <h1 className={styles.title}>{title}</h1>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>

            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className={styles.breadcrumbs}>
                {breadcrumbs.map((crumb, index) => (
                  <span key={index} className={styles.breadcrumbItem}>
                    {crumb.onClick ? (
                      <button
                        className={styles.breadcrumbButton}
                        onClick={crumb.onClick}
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <span className={styles.breadcrumbSeparator}>/</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>

          {/* Action Buttons */}
          {buttons && buttons.length > 0 && (
            <div className={styles.buttonGroup}>
              {buttons.map((button, index) => (
                <button
                  key={index}
                  onClick={button.onClick}
                  className={`${styles.button} ${styles[button.variant]}`}
                >
                  {button.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Page Content */}
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </div>
  );
}
